import { useEffect, useState } from "react";
import Link from "next/link";
import { toast } from "sonner";

import { AdminShell } from "@/components/admin/admin-shell";
import { Badge } from "@/components/ui/badge";
import { apiGet, apiPatch } from "@/services/api";

type Vendor = {
  id: string;
  businessName: string;
  slug: string;
  email: string | null;
  phone: string | null;
  status: string;
  commissionRate: number;
  createdAt: string;
  vendorType: { name: string } | null;
  user: { email: string; name: string } | null;
  _count: { products: number; vendorOrders: number };
};

const STATUSES = ["", "PENDING", "ACTIVE", "APPROVED", "SUSPENDED", "REJECTED", "INACTIVE"];

export function AdminVendorsPage() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  async function load() {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const d = await apiGet<{ vendors: Vendor[] }>(`/api/admin/vendors?${params}`);
    setVendors(d?.vendors ?? []);
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);

  async function act(id: string, action: string, body?: unknown) {
    try {
      await apiPatch(`/api/admin/vendors/${id}/${action}`, body as Record<string, unknown>);
      toast.success(`Vendor ${action}d`);
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed");
    }
  }

  return (
    <AdminShell title="Vendors" subtitle="Marketplace sellers">
      <div className="mb-4 flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search vendors…"
          className="rounded border border-slate-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded border border-slate-300 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s || "All statuses"}
            </option>
          ))}
        </select>
      </div>
      <div className="amazon-card divide-y divide-slate-100 dark:divide-white/10">
        {vendors.length === 0 && <p className="p-6 text-sm text-slate-500">No vendors found.</p>}
        {vendors.map((v) => (
          <div key={v.id} className="space-y-2 p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <Link href={`/admin/vendors/${v.id}`} className="font-bold text-amazon-teal break-words">
                  {v.businessName}
                </Link>
                <p className="text-xs text-slate-500 break-words mt-0.5">
                  {v.vendorType?.name ?? "No type"} · {v.user?.email ?? v.email} · {v._count.products} products ·{" "}
                  {v._count.vendorOrders} orders · {v.commissionRate}% commission
                </p>
              </div>
              <Badge tone={v.status === "ACTIVE" || v.status === "APPROVED" ? "success" : v.status === "REJECTED" || v.status === "SUSPENDED" ? "deal" : "prime"}>
                {v.status}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-bold">
              {v.status === "PENDING" && (
                <>
                  <button className="text-emerald-600" onClick={() => act(v.id, "approve")}>Approve</button>
                  <button className="text-red-600" onClick={() => act(v.id, "reject", { reason: "Does not meet requirements" })}>Reject</button>
                </>
              )}
              {(v.status === "ACTIVE" || v.status === "APPROVED") && (
                <button className="text-red-600" onClick={() => act(v.id, "suspend")}>Suspend</button>
              )}
              {(v.status === "SUSPENDED" || v.status === "INACTIVE" || v.status === "REJECTED") && (
                <button className="text-emerald-600" onClick={() => act(v.id, "activate")}>Activate</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
